import React, { useEffect, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { Loading } from 'ljit-react-components';
import { connect } from 'ljit-store-connecter';
import { RouteKeyEnums, checkIsSystemRoutePath } from '../routes';
import { getLocalStorage } from '../../lib/local-storage-utils';
import { LoadingStatusEnum } from '../../lib/enums';
import { usePrevious } from '../../lib/react-utils';
import { authActions } from '../controller';
import { ErrorEventEnum } from '../lib/enums';

const cacheClientLoginCode = getLocalStorage('clientLoginCode');
const { checkAuthAction } = authActions;
const {
	NONE,
	LOADING,
	SUCCESS,
	FAILED,
} = LoadingStatusEnum;
const { INVALID_CLIENT_IP } = ErrorEventEnum;
const {
	LOGIN,
	NOT_FOUND,
	ANONYMOUS_LOGIN,
	CONVERSATION_OVER,
} = RouteKeyEnums;
const propTypes = {
	loadingStatus: PropTypes.oneOf([NONE, LOADING, SUCCESS, FAILED]).isRequired,
	loginLoadingStatus: PropTypes.oneOf([NONE, LOADING, SUCCESS, FAILED]).isRequired,
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired,
		search: PropTypes.string,
	}).isRequired,
	render: PropTypes.func.isRequired,
	checkAuthAction: PropTypes.func.isRequired,
	fetchMeLoadingStatusMessageCode: PropTypes.string,
	hasClosedConversation: PropTypes.bool,
};

function AuthRoute({
	location,
	render,
	loadingStatus,
	loginLoadingStatus,
	checkAuthAction,
	fetchMeLoadingStatusMessageCode,
	hasClosedConversation,
}) {
	const { pathname = '', search = '' } = location;
	const isSystemPath = useMemo(() => checkIsSystemRoutePath(pathname), [pathname]);
	const prevLoginStatus = usePrevious(loginLoadingStatus);
	const isAuthChecking = loadingStatus === NONE || loadingStatus === LOADING;
	const isAuthed = loadingStatus === SUCCESS && !hasClosedConversation;
	// 進過結束頁（該頁已清 JWT、斷 socket、清登入碼）後，在整頁重新載入之前，
	// 任何 app 路由一律導回結束頁。此旗標不會歸零，前提是重新登入必定整頁重載。
	const hasVisitedConversationOverRef = useRef(false);

	useEffect(() => {
		if (!isSystemPath) {
			checkAuthAction();
		}
	}, []);

	useEffect(() => {
		if (pathname === CONVERSATION_OVER) {
			hasVisitedConversationOverRef.current = true;
		}
	}, [pathname]);

	useEffect(() => {
		if (
			!isAuthed &&
			prevLoginStatus !== SUCCESS &&
			loginLoadingStatus === SUCCESS
		) {
			checkAuthAction();
		}
	}, [
		isAuthed,
		loginLoadingStatus,
		prevLoginStatus,
		checkAuthAction,
	]);

	// ===== Render Logic =====
	// SystemPath paths: 不做 auth check / redirect
	if (isSystemPath) {
		return render({ isAuthed: false, location });
	}

	// 進過結束頁後再回到 app 路由（瀏覽器 / iframe host 返回）-> 結束頁
	if (hasVisitedConversationOverRef.current) {
		return (
			<Redirect
				to={{
					pathname: CONVERSATION_OVER,
					state: {
						from: pathname,
					},
				}}
			/>
		);
	}

	// IP invalid -> 404
	if (fetchMeLoadingStatusMessageCode === INVALID_CLIENT_IP) {
		return (
			<Redirect
				to={{
					pathname: NOT_FOUND,
					state: {
						from: pathname,
					},
				}}
			/>
		);
	}

	if (isAuthChecking) {
		return <Loading />;
	}

	// Not authed -> redirect flow
	if (!isAuthed) {
		const query = new URLSearchParams(search);

		// Anonymous-login: ?clientLoginCode=xxx
		if (query.has('clientLoginCode') && pathname !== ANONYMOUS_LOGIN) {
			return (
				<Redirect
					to={{
						pathname: ANONYMOUS_LOGIN,
						state: {
							from: pathname,
						},
						search,
					}}
				/>
			);
		}

		// Code-login login: ?code=xxx
		if (query.has('code') && pathname !== LOGIN) {
			return (
				<Redirect
					to={{
						pathname: LOGIN,
						state: {
							from: pathname,
						},
						search,
					}}
				/>
			);
		}

		// LocalStorage cached clientLoginCode
		if (cacheClientLoginCode) {
			return (
				<Redirect
					to={{
						pathname: ANONYMOUS_LOGIN,
						state: {
							from: pathname,
						},
						search: `?clientLoginCode=${cacheClientLoginCode}`,
					}}
				/>
			);
		}

		// 舊 session 的對話已結束且 URL 未帶新登入碼 -> 結束頁
		if (hasClosedConversation) {
			return (
				<Redirect
					to={{
						pathname: CONVERSATION_OVER,
						state: {
							from: pathname,
						},
					}}
				/>
			);
		}

		// Default: login
		return (
			<Redirect
				to={{
					pathname: LOGIN,
					state: {
						from: pathname,
					},
					search,
				}}
			/>
		);
	}

	return render({ isAuthed: true, location });
}

const MemoizedAuthRoute = memo(AuthRoute, (prevProps, nextProps) => {
	// System route 直接強制更新（回傳 false）
	if (checkIsSystemRoutePath(nextProps.location.pathname)) {
		return false;
	}

	return (
		nextProps.loadingStatus === prevProps.loadingStatus &&
		nextProps.loginLoadingStatus === prevProps.loginLoadingStatus &&
		nextProps.location.pathname === prevProps.location.pathname &&
		nextProps.location.search === prevProps.location.search &&
		nextProps.fetchMeLoadingStatusMessageCode === prevProps.fetchMeLoadingStatusMessageCode
	);
});

function mapStateToProps(state) {
	return {
		loginLoadingStatus: state.auth.get('idLoginLoadingStatus'),
		loadingStatus: state.auth.get('loadingStatus'),
		fetchMeLoadingStatusMessageCode: state.auth.get('fetchMeLoadingStatusMessageCode'),
		hasClosedConversation: state.auth.getIn(['me', 'hasClosedConversation']),
	};
}

function mapDispatchToProps(dispatch) {
	return {
		checkAuthAction: () => dispatch(checkAuthAction()),
	};
}

AuthRoute.propTypes = propTypes;

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MemoizedAuthRoute));
